import { graphql, useMutation } from 'react-relay';

import useHumanVerification from '@/hooks/use-human-verification';
import { relayMutationToPromise } from '@/utils/relay';

import type { useStartCreateOnboardingDraftMutation } from './__generated__/useStartCreateOnboardingDraftMutation.graphql';

/** Creates the server-side draft that holds `/start` answers before sign-in. */
const useStartCreateOnboardingDraft = () => {
  const humanVerification = useHumanVerification('landlord_onboarding_draft');
  const [commit, isCommitting] =
    useMutation<useStartCreateOnboardingDraftMutation>(graphql`
      mutation useStartCreateOnboardingDraftMutation(
        $input: LandlordCreateOnboardingDraftInput!
        $turnstileToken: String!
      ) {
        landlordCreateOnboardingDraft(
          input: $input
          turnstileToken: $turnstileToken
        ) {
          draft {
            id
          }
          failures
        }
      }
    `);

  const createDraft = async (previewId: string): Promise<string | null> => {
    try {
      const turnstileToken = await humanVerification.getToken();
      const { response } = await relayMutationToPromise(commit, {
        variables: { input: { previewId }, turnstileToken },
      });

      return response.landlordCreateOnboardingDraft?.draft?.id ?? null;
    } catch (error) {
      console.error(error);
      return null;
    } finally {
      humanVerification.reset();
    }
  };

  return {
    createDraft,
    isCreating: isCommitting || humanVerification.isVerifying,
    verificationWidget: humanVerification.widget,
  };
};

export default useStartCreateOnboardingDraft;
